import { faGear } from "@fortawesome/free-solid-svg-icons";
import { useState } from "react";
import Button from "../../models/Button";
import { ValueLevels } from "../../models/KnxInstanceData";
import ExtProperties from "./ExtPropeties";
import OnePanelCell from "./OnePanelCell";
import { PanelCell, PanelCells, PanelLayout, PanelLayouts, PannelCellLayouts } from "./PanelLayoutTypes";
import PanelUtils from "./PanelUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PanelConfig } from "./PanelConfig";
import { itx } from "../../translationItx/itx";

function PageHeader(props: { panel: Button; pageIndex: number; panelConfig: PanelConfig }) {
  const { panel, pageIndex, panelConfig } = props;

  const [extPropertiesOpen, setExtPropertiesOpen] = useState(false);

  const templateId = panelConfig.templateId || "";
  const pageTitleId = panelConfig.pageTitleId || "";

  const pageInstanceId = panelConfig.pageInstanceId?.(pageIndex);

  const title = panel.knxInstanceData.getValue(pageTitleId, pageInstanceId) || "";

  let pageLayouts = panelConfig.horizontalPageLayouts;

  if (PanelUtils.layoutIsVertial(panel, panelConfig)) {
    pageLayouts = panelConfig.verticalPageLayouts;
  }

  const options = new Array<number>();
  for (const idx in pageLayouts) {
    options.push(parseInt(idx));
  }
  options.sort((a, b) => a - b);

  const currentTemplateNumber = parseInt(panel.knxInstanceData.getValue(templateId, pageInstanceId) || "1");

  function applyValue(newValue: number) {
    if (newValue === currentTemplateNumber) return;

    panel.knxInstanceData.setValue(ValueLevels.interactive, newValue.toString(), templateId, pageInstanceId ?? "", "");
    panel.reEvalKnxMetadataNeeded = true;
    panel.raiseOnChange("panelPageLayouts");
  }

  function snapToValid(rawValue: number): number {
    if (options.length === 0) return currentTemplateNumber;
    if (options.includes(rawValue)) return rawValue;

    if (rawValue > currentTemplateNumber) {
      // nächst größerer gültiger Wert
      const next = options.find((o) => o > currentTemplateNumber);
      return next ?? options[options.length - 1];
    }
    if (rawValue < currentTemplateNumber) {
      // nächst kleinerer gültiger Wert
      const prev = [...options].reverse().find((o) => o < currentTemplateNumber);
      return prev ?? options[0];
    }
    return currentTemplateNumber;
  }

  function onChangeTitle(e: React.ChangeEvent<HTMLInputElement>) {
    const newValue = e.target.value;
    if (newValue === title) return;
    panel.knxInstanceData.setValue(ValueLevels.interactive, newValue, pageTitleId, pageInstanceId ?? "", "");
    panel.raiseOnChange("title");
  }

  function onChangePageTemplate(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = parseInt(e.target.value);
    if (isNaN(raw)) return;

    applyValue(snapToValid(raw));
  }

  function onKeyDownPageTemplate(e: React.KeyboardEvent<HTMLInputElement>) {
    // Tippen verhindern: nur Pfeiltasten und Navigation erlauben
    const allowed = [
      "ArrowUp", // no itx
      "ArrowDown", // no itx
      "Tab", // no itx
      "Escape", // no itx
      "Enter", // no itx
    ];
    if (!allowed.includes(e.key)) {
      e.preventDefault();
    }
  }

  return (
    <div className="estadaRow w100 pageHeader" style={{ justifyContent: "flex-start" }}>
      {pageIndex === -1 && <div className="pageHeaderLeft">{itx("OnePanelPage.homePage", "Home Page")}</div>}
      {pageIndex !== -1 && (
        <div className="pageHeaderLeft">
          {itx("OnePanelPage.page", "Page:")} {pageIndex + 1}
        </div>
      )}

      {pageTitleId && pageInstanceId && (
        <div className="estadaRow" style={{ marginLeft: "10px", marginRight: "10px" }}>
          <div style={{ marginRight: "10px" }}>{itx("OnePanelPage.title", "Title:")}</div>
          <input value={title} onChange={onChangeTitle} />
        </div>
      )}

      <div style={{ flexGrow: "1" }} />

      {templateId && pageInstanceId && (
        <div className="pageHeaderRight">
          {itx("OnePanelPage.layout", "Layout:")}
          <input
            type="number"
            value={currentTemplateNumber}
            step="1"
            min={options[0] ?? 1}
            max={options[options.length - 1] ?? 1}
            style={{ border: "none", marginLeft: "10px" }}
            onChange={onChangePageTemplate}
            onKeyDown={onKeyDownPageTemplate}
          />
        </div>
      )}

      {panelConfig.extPageProperties && (
        <div
          className="button circle"
          style={{
            position: "relative",
            width: "30px",
            height: "30px",
            cursor: "pointer",
            backgroundColor: extPropertiesOpen ? "var(--primary-color)" : undefined,
          }}
          onClick={() => setExtPropertiesOpen(true)}
        >
          <FontAwesomeIcon icon={faGear} />
          {extPropertiesOpen && (
            <div style={{ position: "relative", top: "0", right: "0", width: "0px" }}>
              <ExtProperties
                title={"Eigenschaften für Seite " + (pageIndex + 1)}
                panel={panel}
                panelMappingConfig={panelConfig}
                pageIndex={pageIndex}
                extProperties={panelConfig.extPageProperties}
                onClose={() => setExtPropertiesOpen(false)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function OnePanelPage(props: {
  panel: Button;
  redraw: () => void;
  pageIndex: number;
  panelCells: PanelCells;
  panelConfig: PanelConfig;
}) {
  const { panel, redraw, pageIndex, panelCells, panelConfig } = props;

  const cellElements: any = [];

  let gridTemplateColumns = 1;
  let gridTemplateRows = 1;

  let pageLayout = PanelUtils.getPageLayout(panel, panelConfig, pageIndex);

  if (pageLayout) {
    gridTemplateColumns = pageLayout.gridWidth;
    gridTemplateRows = pageLayout.gridHeight;

    for (const cellLayout of pageLayout.cells) {
      let cell = panelCells.cells.find((c) => c.idx === cellLayout.idx && c.page === pageIndex);
      if (!cell) {
        cell = {
          functionTag: "",
          connectedObjectID: "",
          page: pageIndex,
          idx: cellLayout.idx,
        };
      }

      cellElements.push(
        <OnePanelCell
          panel={panel as Button}
          key={`${cellLayout.idx}-${cellLayout.x}-${cellLayout.y}`}
          redraw={redraw}
          panelCells={panelCells}
          panelMappingConfig={panelConfig}
          pageIndex={pageIndex}
          cell={cell}
          row={cellLayout.y}
          column={cellLayout.x}
          width={cellLayout.w ?? 1}
          height={cellLayout.h ?? 1}
          idx={cellLayout.idx}
        />,
      );
    }
  } else {
    //
    // simple grid layout
    // Evrey Cell is 1  unit width and height
    //
    // panelMappingConfig.rows &&
    // panelMappingConfig.columns
    //      maybe just values or a functions that returns a number
    //

    gridTemplateColumns = PanelUtils.getColumns(panel, panelConfig, pageIndex);
    gridTemplateRows = PanelUtils.getRows(panel, panelConfig, pageIndex);

    for (let rI = 0; rI < gridTemplateRows; rI++) {
      for (let cI = 0; cI < gridTemplateColumns; cI++) {
        let cell: PanelCell | undefined = panelCells.cells.find(
          (c) => c.column === cI && c.row === rI && c.page === pageIndex,
        );

        cellElements.push(
          <OnePanelCell
            panel={panel as Button}
            key={`${cI}-${rI}`}
            redraw={redraw}
            panelCells={panelCells}
            panelMappingConfig={panelConfig}
            pageIndex={pageIndex}
            cell={cell}
            row={rI + 1}
            column={cI + 1}
            width={1}
            height={1}
            idx={undefined}
          />,
        );
      }
    }
  }

  return (
    <div className="panelPage estadaColumn w100 left" key={pageIndex}>
      <PageHeader panel={panel} pageIndex={pageIndex} panelConfig={panelConfig} />
      <div
        id="pageCells"
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${gridTemplateColumns}, max-content)`,
          gridTemplateRows: `repeat(${gridTemplateRows}, 1fr)`,
          overflowX: "auto",
        }}
      >
        {cellElements}
      </div>
    </div>
  );
}
